/**
 * Page de liste des entreprises (admin)
 */

import { useState } from 'react';
import { useAdminEntreprises } from '@/hooks/admin/useAdminEntreprises';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, ChevronLeft, ChevronRight, Building2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function AdminEntreprises() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const { entreprises, totalCount, totalPages, loading, error } =
    useAdminEntreprises({
      page,
      pageSize: 20,
      search,
    });

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getPlanBadge = (plan: string | null) => {
    switch (plan) {
      case 'entreprise':
        return <Badge className="bg-purple-500">Entreprise</Badge>;
      case 'pro':
        return <Badge className="bg-blue-500">Pro</Badge>;
      case 'artisan':
        return <Badge className="bg-green-500">Artisan</Badge>;
      default:
        return <Badge variant="outline">Free</Badge>;
    }
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'active':
        return <Badge variant="default">Actif</Badge>;
      case 'trialing':
        return <Badge className="bg-amber-500">Essai</Badge>;
      case 'past_due':
        return <Badge variant="destructive">Impaye</Badge>;
      case 'canceled':
        return <Badge variant="secondary">Annule</Badge>;
      default:
        return <Badge variant="outline">-</Badge>;
    }
  };

  const getUsagePercent = (used: number, limit: number) => {
    if (limit === -1) return 0; // Illimite
    return Math.min(100, Math.round((used / limit) * 100));
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">Erreur : {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Entreprises</h1>
          <p className="text-muted-foreground">
            {totalCount} entreprise{totalCount > 1 ? 's' : ''} inscrite
            {totalCount > 1 ? 's' : ''}
          </p>
        </div>
        <Building2 className="h-10 w-10 text-muted-foreground" />
      </div>

      {/* Recherche */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par nom ou SIRET..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className="pl-10"
              />
            </div>
            <Button onClick={handleSearch}>Rechercher</Button>
          </div>
        </CardContent>
      </Card>

      {/* Tableau */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des entreprises</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Proprietaire</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Chantiers</TableHead>
                    <TableHead>Usage OCR</TableHead>
                    <TableHead>Usage IA</TableHead>
                    <TableHead>Inscription</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entreprises.map((entreprise) => (
                    <TableRow key={entreprise.id}>
                      <TableCell className="font-medium">
                        <div>
                          <p>{entreprise.nom}</p>
                          {entreprise.siret && (
                            <p className="text-xs text-muted-foreground">
                              {entreprise.siret}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p>{entreprise.proprietaire_nom || '-'}</p>
                          <p className="text-xs text-muted-foreground">
                            {entreprise.proprietaire_email}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>{getPlanBadge(entreprise.plan)}</TableCell>
                      <TableCell>{getStatusBadge(entreprise.status)}</TableCell>
                      <TableCell className="text-center">
                        {entreprise.chantiers_count}
                      </TableCell>
                      <TableCell>
                        <div className="w-24">
                          <Progress
                            value={getUsagePercent(
                              entreprise.ocr_used,
                              entreprise.ocr_limit
                            )}
                            className="h-2"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            {entreprise.ocr_used}/{entreprise.ocr_limit === -1 ? '∞' : entreprise.ocr_limit}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="w-24">
                          <Progress
                            value={getUsagePercent(
                              entreprise.ia_used,
                              entreprise.ia_limit
                            )}
                            className="h-2"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            {entreprise.ia_used}/{entreprise.ia_limit === -1 ? '∞' : entreprise.ia_limit}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {format(new Date(entreprise.created_at), 'dd MMM yyyy', {
                          locale: fr,
                        })}
                      </TableCell>
                    </TableRow>
                  ))}
                  {entreprises.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="text-center text-muted-foreground py-8"
                      >
                        Aucune entreprise trouvee
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {page} sur {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Precedent
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Suivant
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
